/**
 * Allowed Connector status transitions per OCPP 1.6J §4.2 table 3.
 *
 * This is the simulator's *operator* policy: what a tester clicking the
 * status dropdown is allowed to choose given the connector's current
 * status. It mirrors the OCPP-spec state diagram with one liberty:
 * `Faulted` is reachable from EVERY state because the simulator's job
 * includes injecting fault conditions on demand. `Faulted → Available`
 * is the recovery path.
 *
 * Pure data + a pure function — no imports above lib. Imported by the
 * UI dropdown and by useStationStore.setConnectorStatus for symmetric
 * gating (UI greys out illegal options; the store refuses if the UI
 * was tricked).
 */
import { ConnectorStatus } from './types/enums';

const A = ConnectorStatus.Available;
const Prep = ConnectorStatus.Preparing;
const Ch = ConnectorStatus.Charging;
const SusEV = ConnectorStatus.SuspendedEV;
const SusEVSE = ConnectorStatus.SuspendedEVSE;
const Fin = ConnectorStatus.Finishing;
const Res = ConnectorStatus.Reserved;
const Una = ConnectorStatus.Unavailable;
const Flt = ConnectorStatus.Faulted;

/**
 * Map: current status → set of statuses the operator may manually
 * transition into. Faulted is appended to every entry below (recovery is
 * Faulted → Available).
 */
const TRANSITIONS: Readonly<Record<ConnectorStatus, readonly ConnectorStatus[]>> = {
  [A]: [Prep, Res, Una, Flt],
  [Prep]: [A, Ch, Fin, Flt],
  [Ch]: [SusEV, SusEVSE, Fin, Flt],
  [SusEV]: [Ch, SusEVSE, Fin, Flt],
  [SusEVSE]: [Ch, SusEV, Fin, Flt],
  [Fin]: [A, Una, Flt],
  [Res]: [A, Prep, Una, Flt],
  [Una]: [A, Flt],
  [Flt]: [A],
};

/** True iff `next` is reachable from `current` via a single operator click. */
export function canTransition(current: ConnectorStatus, next: ConnectorStatus): boolean {
  if (current === next) return false;
  return TRANSITIONS[current].includes(next);
}

/** All statuses (including current, marked allowed=false) for dropdown rendering. */
export interface StatusOption {
  readonly value: ConnectorStatus;
  readonly allowed: boolean;
  readonly current: boolean;
}

export function statusOptionsFor(current: ConnectorStatus): readonly StatusOption[] {
  return ALL_STATUSES.map((value) => ({
    value,
    allowed: canTransition(current, value),
    current: value === current,
  }));
}

const ALL_STATUSES: readonly ConnectorStatus[] = [
  A,
  Prep,
  Ch,
  SusEV,
  SusEVSE,
  Fin,
  Res,
  Una,
  Flt,
];
